import { cloneElement, isValidElement, useEffect, useRef, useState, type ReactElement, type ReactNode, type RefObject } from 'react'
import { fbt } from 'fbtee'
import { useLocation } from 'react-router-dom'
import { MoreHorizontal, Smartphone, X } from 'lucide-react'
import type { ScheduleFilter } from './scheduleFilter'
import { track, type ScheduleFab, type ScheduleSurface } from '../../../lib/analytics'
import { useGuestGuard } from '../../../hooks/useGuestGuard'
import { openFeedback } from '../../../lib/feedbackBridge'
import { usePeekTip } from '../../../lib/nuxTips'
import { cn } from '../../../lib/utils'
import type { Stage } from '../../../types'
import { FilterButton } from './FilterButton'
import { FeedbackFab } from '../../../components/FeedbackFab'
import { OptionChip } from '../../../components/OptionChip'
import { SettingsButton } from './SettingsButton'
import { AddMarkerButton } from './AddMarkerButton'
import { useScheduleMarkersContext } from './scheduleMarkersContext'

import { ScheduleSettingsDialog } from './ScheduleSettingsDialog'
import { FestivalModeFab } from './FestivalModeFab'
import { FilterTipBody } from './FilterTipBody'
import { OneShotTip, type OneShotTipDismissReason } from '../../../components/OneShotTip'
import { fabGlassClass } from '@/lib/glass'

// While festival mode holds the filter's visible slot, a filter that's on would
// show nowhere but the dimmed grid — so the trigger carries a dot in its color.
const FILTER_DOT: Record<Exclude<ScheduleFilter, 'none'>, string> = {
  interested: 'bg-interested',
  going: 'bg-going',
  favorites: 'bg-amber-400',
}

// The schedule's floating options control, in two tiers. The always-visible tier
// sits above the trigger as bare icons and grows text labels when expanded: share,
// plus festival mode when the event has one (it's the schedule's main call to
// action) — otherwise the filter takes that slot. The hidden tier (filter when
// festival mode displaced it, add line, lock screen image, settings, feedback)
// only appears on expand.
//
// The always-visible tier's icons say little on their own, so on a fresh visit
// they briefly grow their labels and collapse again — once per session —
// before the user ever opens the trigger.
export function ScheduleFabs({
  eventId,
  surface,
  filter,
  onCycleFilter,
  allStagesWithVisibility,
  hasCustomSettings,
  onReorder,
  onToggleVisibility,
  onReset,
  festivalMode,
  shareFab,
  onWallpaper,
  rootRef,
  hidden = false,
  peekShift = 0,
  filterTip,
}: {
  eventId: string
  surface: ScheduleSurface
  filter: ScheduleFilter
  onCycleFilter: () => void
  allStagesWithVisibility: Array<Stage & { hidden: boolean }>
  hasCustomSettings: boolean
  onReorder: (stageIds: string[]) => void
  onToggleVisibility: (stageId: string) => void
  onReset: () => void
  festivalMode?: { eventId: string; startDate: string; endDate?: string | null }
  shareFab?: ReactNode
  /** Own schedule only: opens the lock screen image dialog. */
  onWallpaper?: () => void
  rootRef?: RefObject<HTMLDivElement | null>
  /** Tuck the stack against the screen edge — it's parked over a set nothing can scroll out from under it. Ignored while expanded. */
  hidden?: boolean
  /** How far right a hidden stack slides — leaves a sliver peeking at the screen edge. */
  peekShift?: number
  /** Festival mode's one-time coachmark on the filter chip (`useFilterTip`). */
  filterTip?: { open: boolean; dismiss: (via: OneShotTipDismissReason) => void; retire: () => void }
}) {
  const [open, setOpen] = useState(false)
  const [settingsOpen, setSettingsOpen] = useState(false)
  const { isGuest, prompt } = useGuestGuard()
  const { openCreate } = useScheduleMarkersContext()
  const location = useLocation()
  const { peeking, endPeek } = usePeekTip('schedule_fab_labels')
  const labeled = open || peeking
  const filterTucked = !!festivalMode && !open && filter !== 'none'
  const [filterAnchor, setFilterAnchor] = useState<HTMLDivElement | null>(null)
  const ownRef = useRef<HTMLDivElement>(null)
  const stackRef = rootRef ?? ownRef

  // An open menu collapses as soon as the user touches anything outside it or
  // scrolls the grid — the touch itself still goes through.
  useEffect(() => {
    if (!open) return
    const close = (e: Event) => {
      if (!stackRef.current?.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('pointerdown', close, true)
    document.addEventListener('scroll', close, true)
    return () => {
      document.removeEventListener('pointerdown', close, true)
      document.removeEventListener('scroll', close, true)
    }
  }, [open, stackRef])

  function trackFab(fab: ScheduleFab) {
    track('schedule_fab_tap', { fab, surface, event_id: eventId })
  }

  function handleSettingsTap() {
    trackFab('settings')
    setOpen(false)
    if (isGuest) {
      prompt({
        intent: 'schedule_settings',
        title: fbt('Sign up to customize your schedule', 'Guest upsell title for schedule settings'),
        description: fbt(
          'Create an account to save your stage order and hidden stages.',
          'Guest upsell description for schedule settings',
        ),
      })
      return
    }
    setSettingsOpen(true)
  }

  function handleAddMarkerTap() {
    trackFab('marker')
    setOpen(false)
    if (isGuest) {
      prompt({
        intent: 'schedule_marker',
        title: fbt('Sign up to add your own lines', 'Guest upsell title for adding a custom line to the schedule'),
        description: fbt(
          'Create an account to mark dinner, meetups or anything else on your schedule, and share them with your crew.',
          'Guest upsell description for adding a custom line to the schedule',
        ),
        afterSignIn: { pathname: location.pathname, search: location.search },
      })
      return
    }
    openCreate()
  }

  // No guest wall: the image is made on the device from picks it already has.
  function handleWallpaperTap() {
    trackFab('wallpaper')
    setOpen(false)
    onWallpaper?.()
  }

  function handleFeedbackTap() {
    trackFab('feedback')
    setOpen(false)
    if (isGuest) {
      prompt({
        intent: 'support',
        title: fbt('Sign up to send feedback', 'Guest upsell title for the schedule feedback button'),
      })
      return
    }
    openFeedback()
  }

  const toggleLabel = open
    ? fbt('Close options', 'Accessible label to collapse the schedule options FAB')
    : fbt('More options', 'Accessible label to expand the schedule options FAB')

  // Share is always-visible, so it toggles icon-only ↔ labeled with the FAB. It
  // arrives as an opaque node the parent built, so inject `expanded` into it.
  const shareChip = isValidElement(shareFab)
    ? cloneElement(shareFab as ReactElement<{ expanded?: boolean }>, { expanded: labeled })
    : shareFab

  // Hidden tier, kept mounted so it can transition both ways. It sits between the
  // always-visible chips and the trigger: as its height animates open, the flow
  // reflows each frame and physically slides the always-visible chips upward.
  const filterChip = (expanded: boolean) => (
    <FilterButton
      status={filter}
      onCycle={() => {
        trackFab('filter')
        filterTip?.retire()
        onCycleFilter()
      }}
      expanded={expanded}
    />
  )

  const hiddenItems = [
    festivalMode && filterChip(open),
    <AddMarkerButton key="marker" onClick={handleAddMarkerTap} />,
    onWallpaper && (
      <OptionChip
        key="wallpaper"
        icon={<Smartphone className="size-5" />}
        label={fbt('Wallpaper', 'Schedule chip label to make a lock screen image of a day')}
        title={fbt('Make a lock screen image of your schedule', 'Accessible label for the schedule wallpaper chip')}
        onClick={handleWallpaperTap}
      />
    ),
    <SettingsButton key="settings" onClick={handleSettingsTap} />,
    <FeedbackFab key="feedback" onClick={handleFeedbackTap} />,
  ].filter(Boolean)

  // The stack is as wide as its widest chip — and the hidden tier's chips always
  // carry their labels, only clipped vertically — so the container itself must
  // let touches through, or it blocks scrolling the grid beside the icons.
  return (
    <div
      ref={stackRef}
      inert={hidden && !open}
      className={cn(
        'pointer-events-none absolute bottom-[calc(max(var(--nav-scroll-pad),0px)+1.25rem)] right-1 z-45 flex flex-col items-end transition-[translate] duration-300 ease-[cubic-bezier(0.22,1,0.36,1)]',
        // Desktop has no end gutter for the sliver to peek into — fade out there.
        hidden && !open && 'translate-x-(--fab-peek-x) md:translate-x-[calc(100%+0.5rem)] md:[--fab-opacity:0]',
      )}
      style={{ '--fab-peek-x': `${peekShift}px` } as React.CSSProperties}
    >
      <div className="flex flex-col items-end gap-2 *:pointer-events-auto">
        {shareChip}
        {festivalMode ? (
          <FestivalModeFab
            eventId={festivalMode.eventId}
            startDate={festivalMode.startDate}
            endDate={festivalMode.endDate}
            onTap={() => trackFab('festival_mode')}
            expanded={labeled}
          />
        ) : (
          <div ref={setFilterAnchor}>{filterChip(labeled || !!filterTip?.open)}</div>
        )}
      </div>

      <div
        className={cn(
          'grid transition-all duration-300 ease-[cubic-bezier(0.22,1,0.36,1)]',
          open ? 'grid-rows-[1fr] pt-2' : 'grid-rows-[0fr]',
        )}
      >
        <div className="overflow-hidden flex flex-col items-end gap-2">
          {hiddenItems.map((item, i) => (
            <div
              key={i}
              className={cn(
                'pointer-events-auto transition-[translate] duration-300 ease-[cubic-bezier(0.34,1.4,0.64,1)]',
                open ? 'translate-y-0' : 'translate-y-2 [--fab-opacity:0]',
              )}
              style={{ transitionDelay: open ? `${i * 55}ms` : '0ms' }}
            >
              {item}
            </div>
          ))}
        </div>
      </div>

      <button
        type="button"
        onClick={() => {
          endPeek()
          setOpen(v => !v)
        }}
        title={toggleLabel}
        aria-label={toggleLabel}
        aria-expanded={open}
        className={cn(
          'pointer-events-auto relative mt-2 shrink-0 flex items-center justify-center size-12 rounded-full cursor-pointer opacity-(--fab-opacity) transition-[color,background-color,border-color,opacity] duration-300',
          fabGlassClass,
          open ? 'bg-card/90 text-foreground' : 'text-foreground hover:bg-card/90',
        )}
      >
        <span className={cn('transition-transform duration-300 ease-[cubic-bezier(0.34,1.4,0.64,1)]', open && 'rotate-90')}>
          {open ? <X className="size-6" /> : <MoreHorizontal className="size-6" />}
        </span>
        {filterTucked && (
          <span
            data-testid="schedule-filter-dot"
            className={cn('absolute top-0.5 right-0.5 size-3 rounded-full ring-2 ring-background', FILTER_DOT[filter as Exclude<ScheduleFilter, 'none'>])}
          />
        )}
      </button>
      {filterTip && (
        <OneShotTip
          open={filterTip.open && !hidden}
          onDismiss={filterTip.dismiss}
          anchor={filterAnchor}
          side="left"
          body={<FilterTipBody />}
        />
      )}
      <ScheduleSettingsDialog
        open={settingsOpen}
        onOpenChange={setSettingsOpen}
        stages={allStagesWithVisibility}
        onReorder={onReorder}
        onToggleVisibility={onToggleVisibility}
        onReset={onReset}
        hasCustomSettings={hasCustomSettings}
      />
    </div>
  )
}
